import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../lib/LanguageContext";
import { LanguageToggle } from "./LanguageToggle";

export const Header = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const links = [
    { href: "#home", label: t("nav_home"), key: "home" },
    { href: "#apartments", label: t("nav_apartments"), key: "apartments" },
    { href: "#facilities", label: t("nav_facilities"), key: "facilities" },
    { href: "#location", label: t("nav_location"), key: "location" },
    { href: "#gallery", label: t("nav_gallery"), key: "gallery" },
    { href: "#contact", label: t("nav_contact"), key: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#faf9f6]/80 border-b border-[#0a192f]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <a
          href="#home"
          data-testid="logo-link"
          className={`group flex items-baseline gap-2 ${
            scrolled ? "text-[#0a192f]" : "text-white"
          }`}
        >
          <span className="font-serif italic text-2xl md:text-3xl leading-none">
            Athina
          </span>
          <span className="text-[10px] uppercase tracking-[0.32em] opacity-80">
            {t("brand_villas")}
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-link-${l.key}`}
              className={`text-[12px] uppercase tracking-[0.22em] transition-colors ${
                scrolled
                  ? "text-[#0a192f]/70 hover:text-[#1a5b73]"
                  : "text-white/85 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
          <LanguageToggle dark={scrolled} />
          <a
            href="#contact"
            data-testid="nav-book-now"
            className="bg-[#1a5b73] hover:bg-[#134456] text-white px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] transition-colors"
          >
            {t("nav_book_now")}
          </a>
        </nav>

        <div className="flex md:hidden items-center gap-3">
          <LanguageToggle dark={scrolled} />
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className={`p-2 ${scrolled ? "text-[#0a192f]" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#faf9f6] border-t border-[#0a192f]/10 px-6 py-6 flex flex-col gap-5"
        >
          {links.map((l) => (
            <a
              key={l.key}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.key}`}
              className="text-sm uppercase tracking-[0.22em] text-[#0a192f]/80"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            data-testid="mobile-book-now"
            className="mt-2 bg-[#1a5b73] text-white px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] text-center"
          >
            {t("nav_book_now")}
          </a>
        </div>
      )}
    </header>
  );
};
